/**
 * SPEC §11 – urenregistratie: geschreven tijd naast de schatting.
 * Geschat stuurt de sprint (capaciteit, velocity); besteed gaat van het budget af.
 */
import { addDagen } from './datum';
import type { ISODate } from './types';

/** Afronden op een kwartier (SPEC §11.2): naar het dichtstbijzijnde kwartier, minimaal 15 minuten. */
export function rond15(minuten: number): number {
  if (!(minuten > 0)) return 0;
  return Math.max(15, Math.round(minuten / 15) * 15);
}

/** "1,5" · "1.5" · "1:30" · "90m" · "2u" → minuten (afgerond op 15); leeg = 0; ongeldig = null. */
export function leesDuur(invoer: string): number | null {
  const s = invoer.trim().toLowerCase().replace(',', '.');
  if (!s) return 0;
  let m: RegExpMatchArray | null;
  if ((m = s.match(/^(\d+):(\d{1,2})$/))) return rond15(Number(m[1]) * 60 + Number(m[2]));
  if ((m = s.match(/^(\d+(?:\.\d+)?)\s*m(in)?$/))) return rond15(Number(m[1]));
  if ((m = s.match(/^(\d+(?:\.\d+)?)\s*(u|h|uur)?$/))) return rond15(Number(m[1]) * 60);
  return null;
}

/** 90 → "1:30" */
export function fmtDuur(minuten: number): string {
  const m = Math.round(minuten);
  return `${Math.floor(m / 60)}:${String(m % 60).padStart(2, '0')}`;
}

export type AfwijkingKleur = 'groen' | 'oranje' | 'rood';
/** SPEC §11.3 – ≤ 10% boven de schatting groen, 10–25% oranje, > 25% rood. */
export function afwijkingKleur(pct: number): AfwijkingKleur {
  if (pct > 25) return 'rood';
  if (pct > 10) return 'oranje';
  return 'groen';
}

export interface ItemTijd {
  uren: number; // schatting
  status?: string | null;
  restUren?: number | null;
  besteedMin?: number | null;
}

/**
 * SPEC §11.3 – stand van een item: besteed = geschreven; rest = laatste opgave van de engineer,
 * anders max(0, schatting − besteed); Klaar = 0. Afwijking = besteed + rest − schatting.
 */
export function itemStand(i: ItemTijd) {
  const geschat = i.uren || 0;
  const besteed = (i.besteedMin ?? 0) / 60;
  const rest = i.status === 'KLAAR' ? 0 : i.restUren != null ? Math.max(0, i.restUren) : Math.max(0, geschat - besteed);
  const afwijking = besteed + rest - geschat;
  const pct = geschat > 0 ? (afwijking / geschat) * 100 : besteed + rest > 0 ? 100 : 0;
  const r = (n: number) => Math.round(n * 100) / 100;
  return { geschat: r(geschat), besteed: r(besteed), rest: r(rest), afwijking: r(afwijking), pct: Math.round(pct), kleur: afwijkingKleur(pct) };
}

/** Werkdagen ma t/m vr van de week van `maandag`. */
export function weekDagen(maandag: ISODate): ISODate[] {
  return [0, 1, 2, 3, 4].map((i) => addDagen(maandag, i));
}

/**
 * SPEC §11.4 – burndown op de rest: per werkdag de som van de rest van de items in de sprint zoals die
 * aan het eind van die dag gold. Rest per dag uit de opgaven (`resten`), Klaar (afgerondOp) = 0,
 * zonder opgave de schatting. Dagen na vandaag: null.
 */
export function burndownRest(
  sprint: { id: string },
  items: { id: string; uren: number; sprintId?: string | null; status?: string | null; afgerondOp?: string | Date | null }[],
  resten: { itemId: string; datum: ISODate; restUren: number }[],
  werkdagen: ISODate[],
  vandaag: ISODate,
) {
  const mijn = items.filter((i) => i.sprintId === sprint.id);
  const per = new Map<string, { datum: ISODate; restUren: number }[]>();
  for (const r of resten) {
    const l = per.get(r.itemId) ?? [];
    l.push(r);
    per.set(r.itemId, l);
  }
  for (const l of per.values()) l.sort((a, b) => a.datum.localeCompare(b.datum));
  const af = (x: string | Date | null | undefined) => (x ? (typeof x === 'string' ? x : x.toISOString()).slice(0, 10) : null);
  const restOp = (i: (typeof mijn)[number], d: ISODate) => {
    const k = af(i.afgerondOp);
    if (i.status === 'KLAAR' && k && k <= d) return 0;
    const l = (per.get(i.id) ?? []).filter((r) => r.datum <= d);
    return l.length ? l[l.length - 1].restUren : i.uren;
  };
  const totaal = mijn.reduce((s, i) => s + i.uren, 0);
  const n = werkdagen.length;
  const rd = (x: number) => Math.round(x * 2) / 2;
  return {
    totaal: rd(totaal),
    punten: werkdagen.map((d, i) => ({ dag: d, ideaal: rd(n > 1 ? totaal * (1 - i / (n - 1)) : 0), rest: d <= vandaag ? rd(mijn.reduce((s, it) => s + restOp(it, d), 0)) : null })),
  };
}
